"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { toWebp } from "@/utils/common";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";
const STEP_WIDTH = 140; // Horizontal offset in px between centers of step boxes

interface DeliveryProcessMobileProps {
  data?: any;
}

interface DeliveryProcessTab {
  id: number;
  heading: string;
  description: string;
  icon?: string;
}

export default function DeliveryProcessMobileNew({
  data,
}: DeliveryProcessMobileProps) {
  const tabs: DeliveryProcessTab[] = (data?.slides || []).map((slide: any) => {
    const iconUrl =
      slide?.image?.data?.attributes?.url ||
      slide?.mobile_image?.data?.attributes?.url ||
      slide?.ipad_image?.data?.attributes?.url ||
      slide?.hover_image?.data?.attributes?.url;

    return {
      id: slide.id,
      heading: slide.display_title,
      description: slide.title,
      icon: iconUrl ? toWebp(`${baseUrl}${iconUrl}`) : undefined,
    };
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const swiperRef = useRef<any>(null);

  useEffect(() => {
    setTranslateX(-currentSlide * STEP_WIDTH);
  }, [currentSlide]);

  if (!tabs.length) return null;

  return (
    <section className="relative overflow-hidden bg-white lg:hidden block pt-[56px] lg:pb-[20px]">
      <div className="relative z-[1] mx-auto flex max-w-[1280px] flex-col justify-center px-0">
        {/* Section Heading */}
        <h2 className="mx-auto mb-[32px] max-w-[940px] text-center text-[30px] sm:text-[28px] font-bold leading-[34px] sm:leading-[36px] tracking-[0.5px] text-black px-[20px]">
          {data?.title}
        </h2>

        {/* Timeline Navigation Numbers (Top Indicator) */}
        <div className="relative w-full overflow-hidden h-[92px]">
          {/* Vertical line connecting active centered number box to card top border */}
          <span className="absolute bottom-0 left-1/2 h-[65px] w-[1.5px] -translate-x-1/2 bg-[#B9DDF6] z-[0]" />

          {/* Sliding Track for Step Numbers */}
          <div className="relative left-1/2 top-0 h-[52px] w-0">
            <div
              className="relative h-full"
              style={{
                transform: `translateX(${translateX}px)`,
                transition: isDragging
                  ? "none"
                  : "transform 300ms cubic-bezier(0.25, 1, 0.5, 1)",
              }}
            >
              {/* Connecting Horizontal Line across all step numbers */}
              {tabs.length > 1 && (
                <span
                  className="absolute top-1/2 h-[1.5px] -translate-y-1/2 bg-[#B9DDF6] z-0"
                  style={{
                    left: "0px",
                    width: `${(tabs.length - 1) * STEP_WIDTH}px`,
                  }}
                />
              )}

              {/* Number Boxes */}
              {tabs.map((tab, index) => {
                const isActive = currentSlide === index;
                return (
                  <button
                    key={tab.id ?? index}
                    type="button"
                    aria-label={`Go to step ${index + 1}`}
                    onClick={() => {
                      swiperRef.current?.slideTo(index);
                    }}
                    className={`absolute top-0 flex h-[52px] w-[52px] -translate-x-1/2 items-center justify-center border bg-white text-[18px] font-bold transition-all duration-200 cursor-pointer ${
                      isActive
                        ? "border-[#B9DDF6] text-black shadow-[0_4px_10px_0_#00000014]"
                        : "border-[#B9DDF6] text-black/70 hover:text-black"
                    }`}
                    style={{
                      left: `${index * STEP_WIDTH}px`,
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Swiper Cards Slider */}
        <div className="w-full">
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={16}
            centeredSlides={true}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              setCurrentSlide(swiper.realIndex);
            }}
            onProgress={(swiper, progress) => {
              if (tabs.length > 1) {
                const floatIndex = progress * (tabs.length - 1);
                setTranslateX(-floatIndex * STEP_WIDTH);
              }
            }}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
            onSliderMove={() => setIsDragging(true)}
            onTransitionEnd={() => setIsDragging(false)}
            className="w-full"
          >
            {tabs.map((tab, index) => (
              <SwiperSlide key={tab.id ?? index}>
                <div className="w-full sm:px-2 px-[20px] flex justify-center">
                  <div className="relative mx-auto h-[300px] w-full max-w-[380px] overflow-hidden bg-gradient-to-br from-[#EAF5FD] to-[#DCEEFB] px-[28px] py-[30px] shadow-[0_10px_30px_0_#0086E60F]">
                    <Image
                      src="/images/clutch-landing/card-bg.png"
                      alt=""
                      fill
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                    />
                    <Image
                      src="/images/clutch-landing/card-bg-block.png"
                      alt=""
                      height={111}
                      width={45}
                      className="pointer-events-none absolute bottom-0 right-0"
                    />

                    {/* Corner White Dots */}
                    <span className="absolute left-[10px] top-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />
                    <span className="absolute right-[10px] top-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />
                    <span className="absolute bottom-[10px] left-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />
                    <span className="absolute bottom-[10px] right-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />

                    {/* Icon */}
                    {tab.icon && (
                      <div className="relative mx-auto mb-[16px] h-[56px] w-[56px]">
                        <Image
                          src={tab.icon}
                          alt={tab.heading}
                          fill
                          sizes="56px"
                          className="object-contain"
                        />
                      </div>
                    )}

                    {/* Card Title & Description */}
                    <h3 className="mb-[12px] text-center text-[20px] font-bold leading-[26px] text-black">
                      {tab.heading}
                    </h3>
                    <p className="text-center text-[16px] leading-[22px] text-secondary-text line-clamp-5 relative">
                      {tab.description}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Progress dashes */}
        <div className="mx-auto mt-[24px] flex max-w-[244px] items-center justify-center gap-2">
          {tabs.map((tab, index) => (
            <button
              key={tab.id ?? index}
              type="button"
              aria-label={`Go to step ${index + 1}`}
              onClick={() => swiperRef.current?.slideTo(index)}
              className={`h-1 rounded-full p-0 transition-all duration-300 ${
                currentSlide === index
                  ? "min-w-[140px] flex-1 bg-gradient-to-r from-[#8ABC00] to-[#0086E6]"
                  : "w-4 flex-none bg-[#E2EEF9]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
